/**
 * Client gọi Wood Viewer backend (Spring Boot).
 * Hợp đồng API: design/03-backend-design.md §3.2.
 * Base URL từ env VITE_API_BASE (dev: http://localhost:8666).
 */
import type { SalonType, WvmDocument, WvmLighting } from '../types/wvm';
import type { AiModelDto, RenderDto, RenderParams } from '@wood-viewer/shared';

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

/** Lỗi API có kèm HTTP status để UI phân biệt 404 / 422 / mạng. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ModelMeta {
  name: string;
  source: string;
  partCount: number;
  version: number;
  views: number;
  createdAt: string;
  status: string; // active | blocked
  /** Logo thương hiệu của designer (11): URL công khai đã cache-bust; null = dùng logo mặc định. */
  brandLogoUrl?: string | null;
  /**
   * Cờ per-account của CHỦ model (Console "Dịch vụ Web Viewer"): render an toàn cho di động.
   * `false` = chủ TẮT → viewer khôi phục chất lượng đầy đủ trên mobile. Vắng/`true` = BẬT (mặc định).
   */
  mobileSafeRender?: boolean;
}

/** Một mục gán chuyển động (Author mode). Khớp Requests.MotionEntry của backend. */
export interface MotionEntry {
  partId: number;
  salonType: SalonType;
  drawerGroup: number;
  flip: boolean;
  /** chiều mở ra/vào cho cửa bản lề đứng (2/3), trực giao với `flip`. Cần backend WO-03u nhận field này. */
  swingOut?: boolean;
}

function url(path: string): string {
  return `${BASE}/api/v1${path}`;
}

/**
 * Nối `?edit=`/`&edit=` vào path khi có authKey (link Author xem model PRIVATE).
 * Model public: backend bỏ qua `edit` → vô hại, nên gọi ở MỌI request đọc mà không cần phân nhánh.
 */
function withEdit(path: string, authKey?: string | null): string {
  if (!authKey) return path;
  return `${path}${path.includes('?') ? '&' : '?'}edit=${encodeURIComponent(authKey)}`;
}

async function fail(res: Response): Promise<never> {
  let msg = `HTTP ${res.status}`;
  try {
    const text = await res.text();
    if (text) msg = text;
  } catch {
    /* ignore */
  }
  throw new ApiError(msg, res.status);
}

/** GET payload WVM cho viewer. Backend trả thẳng JSON (application/json). */
export async function getPayload(
  token: string,
  authKey?: string | null,
  signal?: AbortSignal,
): Promise<WvmDocument> {
  // no-store: tránh trình duyệt dùng payload cũ trong cache (max-age=300) sau khi Author lưu motion
  const res = await fetch(url(withEdit(`/models/${encodeURIComponent(token)}`, authKey)), {
    signal,
    cache: 'no-store',
  });
  if (!res.ok) return fail(res);
  return (await res.json()) as WvmDocument;
}

/** Texture resolve cho 1 material (03e §4 + tham số per-model 03g/04e). textureUrl ĐÃ chuẩn hóa về BASE. */
export interface MaterialTexture {
  /** URL ảnh; rỗng khi tầng hiệu dụng là MÀU trơn (dùng `color`). */
  textureUrl: string;
  /** Màu trơn `#RRGGBB` override màu WVM gốc; set ⇒ bỏ qua texture, áp makePhong(color). undefined = dùng texture. */
  color?: string;
  /** kích thước thật 1 lần lặp ảnh (mm) = stepMm hiệu dụng — để tiling theo mm. */
  repeatMmU: number;
  repeatMmV: number;
  // --- tham số per-model 04e (03g public DTO). Default = trung tính → vật liệu không settings render y 04d. ---
  /** dịch gốc vân (mm). */
  shiftMmU: number;
  shiftMmV: number;
  /** xoay thêm (độ), cộng dồn orientation. */
  angleDeg: number;
  /** {0,90,180,270} ép material-level; null = kế thừa per-part `part.textureOrientation` (04d). */
  orientation: number | null;
  mirrored: boolean;
  /** kéo 1 ảnh phủ kín mặt (bỏ tiling/shift). */
  stretch: boolean;
  /** 0..100 (bề mặt). */
  transparency: number;
  reflectivity: number;
  /** 0..100 — no-op v1 (đọc+lưu cho parity, không áp; xem 04e §2). */
  sharpness: number;
  brightness: number;
}

/** Một entry vật liệu thô từ public `/materials` (field mới optional — graceful nếu 03g chưa deploy). */
interface RawMaterial {
  materialId: string;
  textureUrl?: string; // vắng khi vật liệu resolve ra màu trơn (chỉ có `color`)
  color?: string; // #RRGGBB — override màu WVM gốc (loại trừ với textureUrl)
  repeatMmU: number;
  repeatMmV: number;
  shiftMmU?: number;
  shiftMmV?: number;
  angleDeg?: number;
  orientation?: number | null;
  mirrored?: boolean;
  stretch?: boolean;
  transparency?: number;
  reflectivity?: number;
  sharpness?: number;
  brightness?: number;
}

/**
 * GET resolve texture theo tên vật liệu (03e §4). Trả map materialId → texture.
 * Chỉ chứa vật liệu ĐÃ map (vắng = viewer dùng màu trơn).
 *
 * Chuẩn hóa `textureUrl`: lấy path+query rồi ghép lại với BASE hiện hành. Backend trả URL
 * tuyệt đối theo `base-url` (vd http://localhost:8666), nuốt thẳng sẽ (a) vỡ trên mobile LAN
 * vì trỏ "localhost", (b) cross-origin → cần CORS + dễ taint WebGL. Ghép lại theo BASE: dev
 * (BASE rỗng) → tương đối qua Vite proxy (same-origin); prod (BASE set) → tuyệt đối + CORS.
 * Khớp đúng đường getPayload. Xem design/04d §điểm 1.
 */
export async function getMaterials(
  token: string,
  authKey?: string | null,
  signal?: AbortSignal,
): Promise<Map<string, MaterialTexture>> {
  const res = await fetch(url(withEdit(`/models/${encodeURIComponent(token)}/materials`, authKey)), {
    signal,
  });
  if (!res.ok) return fail(res);
  const data = (await res.json()) as { materials?: RawMaterial[] };
  const map = new Map<string, MaterialTexture>();
  for (const m of data.materials ?? []) {
    // Vật liệu resolve ra MÀU trơn: không có textureUrl, chỉ override màu WVM gốc. Tham số bề mặt
    // (transparency/reflectivity/brightness) vẫn honor; tiling/shift/angle bỏ qua (không dùng cho màu).
    if (m.color && !m.textureUrl) {
      map.set(m.materialId, {
        textureUrl: '',
        color: m.color,
        repeatMmU: m.repeatMmU ?? 600,
        repeatMmV: m.repeatMmV ?? 600,
        shiftMmU: 0,
        shiftMmV: 0,
        angleDeg: 0,
        orientation: null,
        mirrored: false,
        stretch: false,
        transparency: m.transparency ?? 0,
        reflectivity: m.reflectivity ?? 0,
        sharpness: 0,
        brightness: m.brightness ?? 0,
      });
      continue;
    }
    const u = new URL(m.textureUrl ?? '', location.origin); // chịu cả URL tuyệt đối lẫn tương đối
    map.set(m.materialId, {
      textureUrl: BASE + u.pathname + u.search,
      repeatMmU: m.repeatMmU,
      repeatMmV: m.repeatMmV,
      // field mới: vắng (03g chưa deploy) → default trung tính = hành vi 04d.
      shiftMmU: m.shiftMmU ?? 0,
      shiftMmV: m.shiftMmV ?? 0,
      angleDeg: m.angleDeg ?? 0,
      orientation: m.orientation ?? null,
      mirrored: m.mirrored ?? false,
      stretch: m.stretch ?? false,
      transparency: m.transparency ?? 0,
      reflectivity: m.reflectivity ?? 0,
      sharpness: m.sharpness ?? 0,
      brightness: m.brightness ?? 0,
    });
  }
  return map;
}

/** GET meta: tên, nguồn, số part, version, lượt xem, ngày tạo. */
export async function getMeta(
  token: string,
  authKey?: string | null,
  signal?: AbortSignal,
): Promise<ModelMeta> {
  const res = await fetch(url(withEdit(`/models/${encodeURIComponent(token)}/meta`, authKey)), {
    signal,
  });
  if (!res.ok) return fail(res);
  return (await res.json()) as ModelMeta;
}

/**
 * GET màu nẹp đã lưu (04a §3 / 03b) — PUBLIC, share-link ẩn danh không cần auth. Trả `{name→hex}`.
 * Model không nẹp / endpoint chưa deploy / lỗi → `{}` (không chặn render; nẹp giữ màu mặc định).
 */
export async function getBandColors(
  token: string,
  authKey?: string | null,
  signal?: AbortSignal,
): Promise<Record<string, string>> {
  try {
    const res = await fetch(
      url(withEdit(`/models/${encodeURIComponent(token)}/band-colors`, authKey)),
      { signal },
    );
    if (!res.ok) return {};
    const data = (await res.json()) as { bandColors?: Record<string, string> };
    return data.bandColors ?? {};
  } catch {
    return {};
  }
}

/** POST ghi nhận lượt xem (analytics, fire-and-forget). */
export async function postView(
  token: string,
  device?: 'pc' | 'mobile',
  authKey?: string | null,
): Promise<void> {
  const q = device ? `?device=${device}` : '';
  try {
    await fetch(url(withEdit(`/models/${encodeURIComponent(token)}/views${q}`, authKey)), {
      method: 'POST',
    });
  } catch {
    /* analytics không chặn trải nghiệm xem */
  }
}

/** PUT motion (Author mode) — xác thực bằng authKey trong ?edit=. */
export async function putMotion(
  token: string,
  authKey: string,
  motions: MotionEntry[],
): Promise<void> {
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/motion?edit=${encodeURIComponent(authKey)}`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(motions),
    },
  );
  if (!res.ok) return fail(res);
}

/**
 * PUT tham số ánh sáng mức model (Author mode) — xác thực bằng authKey trong ?edit=.
 * Mirror endpoint motion. Backend lưu + merge vào payload `/models/{token}` (design 03h).
 */
export async function putLighting(
  token: string,
  authKey: string,
  lighting: WvmLighting,
): Promise<void> {
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/lighting?edit=${encodeURIComponent(authKey)}`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lighting),
    },
  );
  if (!res.ok) return fail(res);
}

/** POST thumbnail PNG (viewer chụp canvas). authKey tùy chọn — backend cho phép ẩn danh. */
export async function uploadThumbnail(
  token: string,
  authKey: string | null,
  png: Blob,
): Promise<void> {
  const q = authKey ? `?edit=${encodeURIComponent(authKey)}` : '';
  const res = await fetch(url(`/models/${encodeURIComponent(token)}/thumbnail${q}`), {
    method: 'POST',
    headers: { 'Content-Type': 'image/png' },
    body: png,
  });
  if (!res.ok) return fail(res);
}

/** URL ảnh QR (PNG) cho share link — dùng trực tiếp trong <img>. */
export function qrUrl(token: string, authKey?: string | null): string {
  return url(withEdit(`/models/${encodeURIComponent(token)}/qr`, authKey));
}

// ----- AI Render (design 04a / 10 §8) -----

/**
 * Chuẩn hóa `imageUrl` backend trả (URL tuyệt đối theo base-url, vd http://localhost:8666) về BASE
 * hiện hành — y hệt cách getMaterials xử lý textureUrl: dev (BASE rỗng) → tương đối qua Vite proxy
 * (same-origin, không taint, không "localhost" trên mobile LAN); prod → tuyệt đối + CORS.
 * Ảnh chưa lưu (saved=false) cần auth → nối `&edit={authKey}` (query đi được với <img>; 03c hướng B).
 */
function normalizeRender(r: RenderDto, authKey: string | null): RenderDto {
  if (!r.imageUrl) return r;
  const u = new URL(r.imageUrl, location.origin);
  let imageUrl = BASE + u.pathname + u.search;
  if (!r.saved && authKey) imageUrl += `${u.search ? '&' : '?'}edit=${encodeURIComponent(authKey)}`;
  return { ...r, imageUrl };
}

/** GET registry model AI theo token (author-mode không có Bearer) → kèm cờ `hasKey` của chủ model. */
export async function getAiModels(token: string, authKey: string): Promise<AiModelDto[]> {
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/ai-models?edit=${encodeURIComponent(authKey)}`),
  );
  if (!res.ok) return fail(res);
  return (await res.json()) as AiModelDto[];
}

/**
 * POST tạo job render (multipart: image PNG + params JSON). Trả id job để poll.
 * 04a: `guideImage` (tùy chọn) = pass cấu trúc đồng trục — gửi kèm khi khóa hình khối (multi-image).
 */
export async function createRender(
  token: string,
  authKey: string,
  image: Blob,
  params: RenderParams,
  guideImage?: Blob,
): Promise<{ id: number; status: string }> {
  const fd = new FormData();
  fd.append('image', image, 'scene.png');
  fd.append('params', JSON.stringify(params));
  if (guideImage) fd.append('guideImage', guideImage, 'guide.png');
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/renders?edit=${encodeURIComponent(authKey)}`),
    { method: 'POST', body: fd },
  );
  if (!res.ok) return fail(res);
  return (await res.json()) as { id: number; status: string };
}

/** GET poll trạng thái 1 job render. imageUrl đã chuẩn hóa (+ &edit nếu ảnh chưa lưu). */
export async function getRender(token: string, authKey: string, id: number): Promise<RenderDto> {
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/renders/${id}?edit=${encodeURIComponent(authKey)}`),
  );
  if (!res.ok) return fail(res);
  return normalizeRender((await res.json()) as RenderDto, authKey);
}

/** POST lưu ảnh render → công khai (hiện gallery). */
export async function saveRender(token: string, authKey: string, id: number): Promise<RenderDto> {
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/renders/${id}/save?edit=${encodeURIComponent(authKey)}`),
    { method: 'POST' },
  );
  if (!res.ok) return fail(res);
  return normalizeRender((await res.json()) as RenderDto, authKey);
}

/** DELETE ảnh render (cả tạm lẫn đã lưu) → giải phóng quota. */
export async function deleteRender(token: string, authKey: string, id: number): Promise<void> {
  const res = await fetch(
    url(`/models/${encodeURIComponent(token)}/renders/${id}?edit=${encodeURIComponent(authKey)}`),
    { method: 'DELETE' },
  );
  if (!res.ok) return fail(res);
}

/** GET gallery công khai (chỉ ảnh saved=true) — không cần auth. */
export async function listRenders(token: string, authKey?: string | null): Promise<RenderDto[]> {
  const res = await fetch(url(withEdit(`/models/${encodeURIComponent(token)}/renders`, authKey)));
  if (!res.ok) return fail(res);
  const data = (await res.json()) as { renders?: RenderDto[] };
  return (data.renders ?? []).map((r) => normalizeRender(r, null));
}
